import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Rocket, Download, ExternalLink, RefreshCw, Eye, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusPill } from "@/components/StatusPill";
import ListingPreview from "@/components/ListingPreview";
import { WhatsAppShareButton } from "@/components/vehicle/WhatsAppShareButton";
import { api, apiError, downloadFile } from "@/lib/api";
import { COMPLIANCE_META, PUB_META, fmtDateTime } from "@/lib/constants";

const CheckIcon = ({ status }) =>
  status === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" /> :
  status === "warn" ? <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" /> :
  <XCircle className="h-4 w-4 text-red-400 shrink-0" />;

export default function PublishTab({ vehicle, reload }) {
  const [preflight, setPreflight] = useState(null);
  const [selected, setSelected] = useState(["mobile_de", "autoscout24", "homepage"]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const load = () => api.get(`/vehicles/${vehicle.id}/preflight`).then((r) => setPreflight(r.data));
  useEffect(() => { load(); }, [vehicle.id, vehicle.publications]); // eslint-disable-line

  const publish = async () => {
    setPublishing(true);
    setResults(null);
    try {
      const r = await api.post(`/vehicles/${vehicle.id}/publish`, { channels: selected });
      setResults(r.data.results);
      const ok = r.data.results.filter((x) => x.success).length;
      toast.success(`Veröffentlichung abgeschlossen: ${ok}/${r.data.results.length} Kanäle erfolgreich (Testmodus)`);
      setConfirmOpen(false);
      reload();
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPublishing(false);
    }
  };

  const unpublish = async (ch) => {
    try {
      await api.post(`/vehicles/${vehicle.id}/publications/${ch}/unpublish`);
      toast.success("Inserat entfernt");
      reload(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sync = async (ch) => {
    try {
      await api.post(`/vehicles/${vehicle.id}/publications/${ch}/sync`);
      toast.success("Änderungen übertragen");
      reload(); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!preflight) return <div className="text-sm text-muted-foreground py-8 text-center">Prüfung wird geladen...</div>;

  const cm = COMPLIANCE_META[preflight.status] || COMPLIANCE_META.blocked;
  const blocked = preflight.status === "blocked";
  const pubs = vehicle.publications || {};
  const publishableChannels = preflight.channels.filter((c) => ["mobile_de", "autoscout24", "homepage"].includes(c.id));

  return (
    <div className="grid lg:grid-cols-12 gap-4">
      <div className="lg:col-span-5 space-y-4">
        <Card data-testid="publishing-preflight-checklist">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-display">Finale Prüfung</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => setPreviewOpen(true)} data-testid="publish-preview-button">
                <Eye className="h-3.5 w-3.5" /> Inseratsvorschau
              </Button>
              <StatusPill label={cm.label} color={cm.color} testId="preflight-status-pill" />
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {preflight.checks.map((c, i) => (
              <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`preflight-check-${i}`}>
                <CheckIcon status={c.status} />
                <div>
                  <span className={c.status === "fail" ? "text-red-300" : c.status === "warn" ? "text-amber-300" : ""}>{c.label}</span>
                  {c.detail && <span className="text-xs text-muted-foreground block">{c.detail}</span>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-display">Teilen & Exporte</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <WhatsAppShareButton vehicle={vehicle} testId="publish-whatsapp-button" label="Per WhatsApp senden" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" />
            <Button variant="outline" size="sm" className="gap-1.5 h-10 border-primary/40 text-primary hover:bg-primary/10" data-testid="export-pdf-button"
              disabled={pdfBusy}
              onClick={() => {
                setPdfBusy(true);
                downloadFile(`/vehicles/${vehicle.id}/export/pdf`, `Expose_${vehicle.bestandsnummer}.pdf`)
                  .then(() => toast.success("PDF-Exposé heruntergeladen"))
                  .catch(() => toast.error("PDF-Export fehlgeschlagen"))
                  .finally(() => setPdfBusy(false));
              }}>
              {pdfBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF-Exposé
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="export-csv-button"
              onClick={() => downloadFile(`/vehicles/${vehicle.id}/export/csv`, `caros_${vehicle.bestandsnummer}.csv`).then(() => toast.success("CSV heruntergeladen")).catch(() => toast.error("Export fehlgeschlagen"))}>
              <Download className="h-3.5 w-3.5" /> CSV-Export
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" data-testid="export-json-button"
              onClick={() => downloadFile(`/vehicles/${vehicle.id}/export/json`, `caros_${vehicle.bestandsnummer}.json`).then(() => toast.success("JSON heruntergeladen")).catch(() => toast.error("Export fehlgeschlagen"))}>
              <Download className="h-3.5 w-3.5" /> JSON / API-Export
            </Button>
            <p className="text-[10px] text-muted-foreground w-full">Diese Exporte sind voll funktional (UTF-8, deutsche Umlaute korrekt) und können für Homepage-Feeds oder DMS-Import genutzt werden.</p>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-7 space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-display">Kanäle</CardTitle>
            <p className="text-xs text-muted-foreground">mobile.de & AutoScout24 laufen im Testmodus (Mock-Adapter) – echte Händler-API-Zugänge können später hinterlegt werden.</p>
          </CardHeader>
          <CardContent className="space-y-2">
            {publishableChannels.map((ch) => {
              const pub = pubs[ch.id];
              return (
                <div key={ch.id} className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`channel-${ch.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <Checkbox checked={selected.includes(ch.id)} disabled={pub?.status === "published"}
                      onCheckedChange={() => setSelected((s) => (s.includes(ch.id) ? s.filter((x) => x !== ch.id) : [...s, ch.id]))}
                      data-testid={`channel-select-${ch.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">{ch.name} <span className="text-[10px] text-sky-300">Testmodus</span></div>
                      {pub?.external_url && (
                        <a href={pub.external_url} target="_blank" rel="noreferrer" className="text-xs text-primary flex items-center gap-1 truncate" onClick={(e) => e.stopPropagation()}>
                          {pub.external_url} <ExternalLink className="h-3 w-3 shrink-0" />
                        </a>
                      )}
                      {pub?.last_error && <div className="text-xs text-red-300">{pub.last_error}</div>}
                      {ch.validation_errors?.length > 0 && !pub && (
                        <div className="text-xs text-amber-300">{ch.validation_errors.join(" · ")}</div>
                      )}
                    </div>
                    {pub ? (
                      <div className="flex items-center gap-1.5">
                        <StatusPill label={PUB_META[pub.status]?.label || pub.status} color={PUB_META[pub.status]?.color} />
                        {pub.status === "update_pending" && (
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => sync(ch.id)} data-testid={`channel-sync-${ch.id}`}>
                            <RefreshCw className="h-3 w-3" /> Sync
                          </Button>
                        )}
                        {["published", "update_pending", "failed"].includes(pub.status) && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => unpublish(ch.id)} data-testid={`channel-unpublish-${ch.id}`}>
                            Entfernen
                          </Button>
                        )}
                      </div>
                    ) : (
                      <StatusPill label="Nicht veröffentlicht" color="bg-slate-500/20 text-slate-300 border-slate-500/30" />
                    )}
                  </div>
                  {pub?.last_published_at && (
                    <div className="text-[10px] text-muted-foreground mt-1.5 pl-7">Zuletzt übertragen: {fmtDateTime(pub.last_published_at)} · Version {pub.payload_version}</div>
                  )}
                </div>
              );
            })}

            {results && (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1" data-testid="publish-results">
                <div className="text-xs font-medium text-emerald-300">Ergebnis der Übertragung</div>
                {results.map((r, i) => (
                  <div key={i} className="text-xs flex items-center gap-2">
                    {r.success ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <XCircle className="h-3.5 w-3.5 text-red-400" />}
                    {r.channel_name || r.channel}: {r.success ? `Online – ${r.external_url}` : (r.errors || []).join(", ")}
                  </div>
                ))}
              </div>
            )}

            <div className="pt-2">
              <Button className="w-full h-12 font-semibold gap-2" disabled={blocked || selected.length === 0 || !preflight.can_publish}
                onClick={() => setConfirmOpen(true)} data-testid="publish-now-button">
                <Rocket className="h-4 w-4" /> Jetzt veröffentlichen ({selected.length} Kanal/Känale)
              </Button>
              {blocked && <p className="text-xs text-red-300 mt-2 text-center">Veröffentlichung blockiert – bitte zuerst die Blocker in der finalen Prüfung lösen.</p>}
              {!preflight.can_publish && !blocked && <p className="text-xs text-amber-300 mt-2 text-center">Veröffentlichung erfordert die Rolle Inhaber oder Manager. Sie können die Freigabe anfordern.</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader><DialogTitle>Veröffentlichung bestätigen</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p>Das Fahrzeug wird an {selected.length} Kanal/Känale übertragen (Testmodus). Keine stille Veröffentlichung – jeder Schritt wird protokolliert.</p>
            {preflight.status === "ready_with_warnings" && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-300">
                Es liegen Warnungen vor: {preflight.warnings.map((w) => w.label).join(" · ")}
              </div>
            )}
            {preflight.status === "confirmation_required" && (
              <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-2.5 text-xs text-orange-300">
                Unbestätigte sensible Angaben werden NICHT im Inserat verwendet.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Abbrechen</Button>
            <Button onClick={publish} disabled={publishing} className="gap-2" data-testid="publish-confirm-button">
              {publishing ? <><Loader2 className="h-4 w-4 animate-spin" /> Wird übertragen...</> : "Bestätigen & veröffentlichen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto p-3 sm:p-4">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Eye className="h-4 w-4 text-primary" /> So sehen Käufer Ihr Inserat</DialogTitle>
          </DialogHeader>
          <ListingPreview vehicle={vehicle} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
